import React, { useState, useRef, useEffect } from 'react';

const RecordButton = ({ localStream, isScreenSharing, screenStream, socketRef, roomId, nickname }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingIndicator, setRecordingIndicator] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // Combine all streams
      const streams = [];
      if (localStream) streams.push(localStream);
      if (isScreenSharing && screenStream) streams.push(screenStream);

      if (streams.length === 0) {
        alert('No media streams available to record');
        return;
      }

      // Use MediaRecorder to record
      const options = {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 2500000
      };

      // Check if MediaRecorder supports the options
      let recorder;
      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        recorder = new MediaRecorder(streams[0], options);
      } else {
        recorder = new MediaRecorder(streams[0]);
      }

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `meeting_${timestamp}.webm`;
        
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        recordedChunksRef.current = [];
        showNotification('🎬 Recording saved successfully');
      };

      recorder.start();
      setIsRecording(true);
      setRecordingIndicator(true);
      showNotification('🔴 Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording: ' + error.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingIndicator(false);
      showNotification('⏸️ Recording stopped');
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const showNotification = (message) => {
    // Create a toast notification
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      z-index: 9999;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
      animation: slideIn 0.3s ease;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  };

  return (
    <>
      <button
        onClick={toggleRecording}
        className={`zoom-control-btn ${isRecording ? 'recording' : 'inactive'}`}
        title={isRecording ? 'Stop Recording' : 'Start Recording'}
        style={isRecording ? { background: 'var(--accent-danger)' } : {}}
      >
        <span className="control-icon" aria-hidden="true">
          {isRecording ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="6" width="12" height="12" rx="2" fill="white"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="8" fill="#ea4335"/>
              <circle cx="12" cy="12" r="8" fill="none" stroke="white" strokeWidth="2"/>
            </svg>
          )}
        </span>
        <span className="control-label">{isRecording ? 'Stop' : 'Record'}</span>
      </button>

      {recordingIndicator && (
        <div className="recording-indicator">
          <span className="recording-dot"></span>
          <span className="recording-text">Recording</span>
        </div>
      )}
    </>
  );
};

export default RecordButton;


